import { FC, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CampaignDetailView } from 'src/pages/ThemeDetail/CampaignDetailHeader'
import { REVIEW_ANALYSIS_CONNECT_ROUTE } from 'src/constants/routes'
import { useReviewAnalysisLinkQuery } from 'src/hooks/useReviewAnalysisDateRange'
import { cn } from 'src/utils/cn'
import { createLink } from 'src/utils/createLink'
import { UpgradePlanModal } from 'src/components/UpgradePlanModal'
import { useGetReviewAnalysisStatusQuery } from 'src/reactQuery/useGetReviewAnalysisStatusQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import { BrandLevelWidgets } from './BrandLevelWidgets'
import { EmptyState } from './EmptyState'
import { LoadingSkeleton } from './LoadingSkeleton'
import {
  ReviewAnalysisHeader,
  ReviewHeaderOptions,
} from './ReviewAnalysisHeader'
import { ReviewAnalysisWidget } from './ReviewAnalysisWidget'
import { ReviewComparison } from './ReviewComparison'
import classes from './reviewAnalysis.module.scss'

export const ReviewAnalysis: FC = () => {
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const navigate = useNavigate()
  const linkQuery = useReviewAnalysisLinkQuery()
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  // Which half of the header's switcher is showing. The comparison is a view of
  // this page rather than a route of its own, as it is in Social Listening.
  const [view, setView] = useState<CampaignDetailView>('general')

  const { data: userFeatures, isLoading: isFeaturesLoading } =
    useGetUserFeaturesQuery()
  const hasAccess = userFeatures?.data?.reviewAnalysisEnabled ?? false

  const { data: statusData, isLoading: isStatusLoading } =
    useGetReviewAnalysisStatusQuery(campaignId)
  const hasPlatforms = statusData?.hasPlatforms ?? false

  if (isFeaturesLoading || isStatusLoading) {
    return <LoadingSkeleton />
  }

  if (!hasAccess) {
    return (
      <>
        <div className={classes.disabledWrapper}>
          <EmptyState
            onEnableClick={() => setIsUpgradeModalOpen(true)}
            type="disabled"
          />
        </div>
        <UpgradePlanModal
          isOpen={isUpgradeModalOpen}
          setIsOpen={setIsUpgradeModalOpen}
        />
      </>
    )
  }

  const header = (options: ReviewHeaderOptions = {}) => (
    <ReviewAnalysisHeader
      embedded={options.embedded}
      onBack={options.onBack}
      onViewChange={setView}
      trail={options.trail}
      view={view}
    />
  )

  // The comparison renders the header itself: its list keeps the page's title
  // inside the card, while a generated report replaces it with a trail back to
  // that list — a difference only that view knows about.
  if (view === 'comparison') {
    return (
      <div className={cn(classes.wrapper, classes.comparisonWrapper)}>
        <ReviewComparison
          header={header}
          onExitComparison={() => setView('general')}
        />
      </div>
    )
  }

  if (hasPlatforms) {
    return (
      <div className={classes.wrapper}>
        {header()}
        <BrandLevelWidgets />
      </div>
    )
  }

  // No platforms yet: the "Start analyzing customer reviews" empty state on
  // the whole page, nothing else (there is nothing to compare or summarise).
  // Its button opens the Connect Review Platforms page, which comes back here
  // once the facility is created.
  return (
    <div className={classes.wrapper}>
      <ReviewAnalysisWidget
        fullPage
        onAddPlatform={() =>
          navigate(
            createLink(REVIEW_ANALYSIS_CONNECT_ROUTE, {
              params: { campaignId },
              queryParams: linkQuery,
            })
          )
        }
      />
    </div>
  )
}
